/**
 * Chunk storage. The world is addressed in 32^3 blocks per chunk, including the
 * vertical axis — BlockWild digs a kilometre down, so unlike the prototype the
 * chunk key carries a y coordinate.
 *
 * This file is a pure container: it knows index math and floor division for
 * negative coordinates, and nothing about generation. Generation lives in
 * `worldgen.ts`; overlay edits live in `world.ts`. That split is what keeps
 * "same seed, same chunk bytes" true no matter who touched the store.
 */

import { BLOCK } from "../data/blocks";

export const CHUNK_SIZE = 32;
export const CHUNK_VOLUME = CHUNK_SIZE * CHUNK_SIZE * CHUNK_SIZE;

export interface Chunk {
  readonly cx: number;
  readonly cy: number;
  readonly cz: number;
  /** Block ids, row-major: index = x + z * 32 + y * 1024 (x fastest, y slowest). */
  readonly blocks: Uint8Array;
}

export function createChunk(cx: number, cy: number, cz: number): Chunk {
  return { cx, cy, cz, blocks: new Uint8Array(CHUNK_VOLUME) };
}

/** Index of a local (0..31) coordinate triple inside `Chunk.blocks`. */
export function blockIndex(lx: number, ly: number, lz: number): number {
  return lx + lz * CHUNK_SIZE + ly * CHUNK_SIZE * CHUNK_SIZE;
}

/**
 * Chunk coordinate of a block coordinate. `Math.floor`, not `>> 5` or
 * truncation — floor division is the only variant that is correct for
 * negative block coordinates (x = -1 belongs to chunk -1, local 31).
 */
export function chunkCoord(c: number): number {
  return Math.floor(c / CHUNK_SIZE);
}

/** Local coordinate (0..31) of a block coordinate inside its chunk. */
export function localCoord(c: number): number {
  return c - chunkCoord(c) * CHUNK_SIZE;
}

export function chunkKey(cx: number, cy: number, cz: number): string {
  return `${cx},${cy},${cz}`;
}

/**
 * A map of generated chunks keyed by "cx,cy,cz". Missing chunks read as
 * `undefined` so the caller can decide between "not generated yet" and air.
 */
export class ChunkStore {
  private readonly chunks = new Map<string, Chunk>();

  get size(): number {
    return this.chunks.size;
  }

  getChunk(cx: number, cy: number, cz: number): Chunk | undefined {
    return this.chunks.get(chunkKey(cx, cy, cz));
  }

  setChunk(chunk: Chunk): void {
    this.chunks.set(chunkKey(chunk.cx, chunk.cy, chunk.cz), chunk);
  }

  /** Block id at world coordinates, or `undefined` when the chunk is missing. */
  getBlock(x: number, y: number, z: number): number | undefined {
    const chunk = this.getChunk(chunkCoord(x), chunkCoord(y), chunkCoord(z));
    if (!chunk) return undefined;
    return chunk.blocks[blockIndex(localCoord(x), localCoord(y), localCoord(z))] ?? BLOCK.AIR;
  }

  /** Write a block at world coordinates, allocating an empty chunk if needed. */
  setBlock(x: number, y: number, z: number, id: number): void {
    const cx = chunkCoord(x);
    const cy = chunkCoord(y);
    const cz = chunkCoord(z);
    const chunk = this.getChunk(cx, cy, cz) ?? createChunk(cx, cy, cz);
    chunk.blocks[blockIndex(localCoord(x), localCoord(y), localCoord(z))] = id;
    this.setChunk(chunk);
  }
}
